/* NEW: UI_REVAMP */
import { Box, Flex, Icon, Text } from '@chakra-ui/react';
import dayjs from 'dayjs';
import React from 'react';
import DatePicker from 'react-datepicker';
import { ReactComponent as CalendarIcon } from '../../assets/svg/calender-icon.svg';
import GoldPatternButton from '../button/GoldPatternButton';
import DateRangeCalendarHeader from './DateRangeCalendarHeader';
import { CALENDAR_MIN_DATE, dateRangeCalendarStyles } from './dateRangeCalendarStyles';

export const DateRangeInput = React.forwardRef(({ startDate, endDate, onClick }, ref) => {
	const formattedStartDate = dayjs(startDate || new Date()).format('YYYY-MM-DD');
	const formattedEndDate = endDate ? dayjs(endDate).format('YYYY-MM-DD') : 'Select date';

	return (
		<Flex
			as='button'
			type='button'
			ref={ref}
			onClick={onClick}
			width='100%'
			height='40px'
			alignItems='center'
			paddingX='16px'
			borderRadius='22px'
			backgroundColor='#F9FAFB'
			color='#000000'
			fontSize='16px'
			fontWeight='400'
		>
			<Text flex='1' textAlign='center'>
				{formattedStartDate}
			</Text>
			<Text flex='0 0 auto' color='#000000' fontWeight='700'>
				→
			</Text>
			<Text flex='1' textAlign='center'>
				{formattedEndDate}
			</Text>
			<Icon as={CalendarIcon} color='#0052FF' boxSize='14px' />
		</Flex>
	);
});

DateRangeInput.displayName = 'DateRangeInput';

export const SingleDateInput = React.forwardRef(({ date, onClick }, ref) => {
	const formattedDate = dayjs(date || new Date()).format('YYYY-MM-DD');

	return (
		<Flex
			as='button'
			type='button'
			ref={ref}
			onClick={onClick}
			width='100%'
			height='40px'
			alignItems='center'
			justifyContent='center'
			paddingX='16px'
			borderRadius='22px'
			backgroundColor='#F9FAFB'
			color='#000000'
			fontSize='16px'
			fontWeight='400'
		>
			<Text flex='1' textAlign='center'>
				{formattedDate}
			</Text>
			<Icon as={CalendarIcon} color='#0052FF' boxSize='14px' />
		</Flex>
	);
});

SingleDateInput.displayName = 'SingleDateInput';

const DateRangeCalendarStage = ({
	label,
	dateRange,
	onChangeDateRange,
	maxDate,
	onClear,
	onConfirm,
	isMobile = false,
	clearLabel = 'Clear All',
	isTextClearButton = false,
	textClearButtonPadding = '0 18px',
	confirmButtonWidth,
	selectsRange = true,
}) => {
	const calendarMaxDate = maxDate || new Date();
	const todayDate = new Date();
	const getDayClassName = (date) => {
		const dayClasses = [];
		const isSingleSelectedDate =
			dateRange[0] && !dateRange[1] && dayjs(date).isSame(dateRange[0], 'day');

		if (dayjs(date).isSame(todayDate, 'day')) dayClasses.push('calendar-day--today');
		if (isSingleSelectedDate) dayClasses.push('calendar-day--single-selected');

		return dayClasses.join(' ') || undefined;
	};

	return (
		<>
			<Text
				fontSize={isMobile ? '14px' : '16px'}
				fontWeight='500'
				color='#111111'
				marginBottom='8px'
			>
				{label}
			</Text>
			{selectsRange ? (
				<DateRangeInput startDate={dateRange[0]} endDate={dateRange[1]} />
			) : (
				<SingleDateInput date={dateRange[0]} />
			)}
			<Box marginTop='12px' sx={dateRangeCalendarStyles}>
				<DatePicker
					inline
					selectsRange={selectsRange}
					selected={selectsRange ? undefined : dateRange[0]}
					startDate={selectsRange ? dateRange[0] : undefined}
					endDate={selectsRange ? dateRange[1] : undefined}
					onChange={(val) => {
						if (selectsRange) {
							onChangeDateRange(val);
						} else {
							onChangeDateRange([val, null]);
						}
					}}
					dateFormat='yyyy-MM-dd'
					minDate={CALENDAR_MIN_DATE}
					maxDate={calendarMaxDate}
					calendarStartDay={1}
					formatWeekDay={(day) => day.slice(0, 2)}
					calendarClassName='date-range-calendar'
					dayClassName={getDayClassName}
					renderDayContents={(day) => <span className='calendar-day-content'>{day}</span>}
					renderCustomHeader={(props) => (
						<DateRangeCalendarHeader
							{...props}
							minDate={CALENDAR_MIN_DATE}
							maxDate={calendarMaxDate}
						/>
					)}
				/>
			</Box>
			<Flex marginTop='14px' gap='8px' width='100%' alignItems='center'>
				{isMobile || isTextClearButton ? (
					<Box
						as='button'
						flex='0 0 auto'
						padding={textClearButtonPadding}
						fontSize='16px'
						fontWeight='700'
						color='#FF5A00'
						lineHeight='100%'
						onClick={onClear}
					>
						{clearLabel}
					</Box>
				) : (
					<Box
						as='button'
						flex='1'
						height='50px'
						padding='12px 10px'
						fontWeight='600'
						borderRadius='100px'
						background='linear-gradient(180deg, #F6D6DE 0%, #F1C7D2 100%)'
						color='#FF3333'
						onClick={onClear}
					>
						{clearLabel}
					</Box>
				)}
				<GoldPatternButton
					flex={confirmButtonWidth ? '0 0 auto' : '1'}
					width={confirmButtonWidth}
					height='52px'
					onClick={onConfirm}
				>
					Confirm
				</GoldPatternButton>
			</Flex>
		</>
	);
};

export default DateRangeCalendarStage;
